import {
  toFiniteNumber,
  toRowLocation,
  toText,
  type LayerAnalyticsCard,
  type LayerAnalyticsChart,
  type LayerAnalyticsFeature,
  type LayerAnalyticsFigure,
  type LayerAnalyticsResult,
  type LayerAnalyticsTable,
  type LayerAnalyticsTableRow,
} from './layerAnalytics.utils';

/*
 * Hurricane template (three cards, one per sublayer of the Active Hurricanes
 * service):
 *   Forecast Cone      figures Storms · Max Wind · Max Gust · Max Surge
 *                      charts  Storm type · Forecast period
 *                      table   Name · Storm Type · Advisory · Wind Gust (kt)
 *   Forecast Position  figures Forecast Points · Max Wind · Max Gust
 *                      charts  Development / type
 *                      table   Name · Date · Wind (kt) · Gust (kt)
 *   Observed Position  figures Observed Points · Latest Max Wind
 *                      table   Name · Date · Wind Speed (kt) · SS
 */

export type HurricaneCardKey = 'cone' | 'forecastPosition' | 'observedPosition';

export type HurricaneColumn = { key: string; header: string };

/** One storm as shown in a card's table, before it becomes a table row. */
export type HurricaneStorm = {
  name: string;
  /** Wind in knots; null when the layer has no usable value. */
  windKnots: number | null;
  /** Display text for the card's extra table columns, keyed by column key. */
  details: Record<string, string>;
  longitude: number | null;
  latitude: number | null;
  extent: LayerAnalyticsTableRow['extent'];
};

/**
 * `windField` is the attribute shown in the card's wind column; the observed
 * layer has no gust field, so it reports sustained wind (INTENSITY).
 * `rankField` picks which feature represents a storm (the highest value wins;
 * the observed layer uses its date instead). The extra columns sit before and
 * after the wind column; their text lives in `HurricaneStorm.details[key]`.
 */
export const HURRICANE_CARDS: {
  key: HurricaneCardKey;
  title: string;
  windLabel: string;
  layerPattern: RegExp;
  windField: string;
  rankField: string;
  columnsBeforeWind: HurricaneColumn[];
  columnsAfterWind: HurricaneColumn[];
}[] = [
  {
    key: 'cone',
    title: 'Hurricane Forecast Cone',
    windLabel: 'Wind Gust (kt)',
    layerPattern: /forecast.*cone/i,
    windField: 'MAX_GUST',
    rankField: 'MAX_GUST',
    columnsBeforeWind: [
      { key: 'stormType', header: 'Storm Type' },
      { key: 'advisory', header: 'Advisory' },
    ],
    columnsAfterWind: [],
  },
  {
    key: 'forecastPosition',
    title: 'Hurricane Forecast Position',
    windLabel: 'Wind (kt)',
    layerPattern: /forecast position/i,
    windField: 'MAXWIND',
    rankField: 'GUST',
    columnsBeforeWind: [{ key: 'date', header: 'Date' }],
    columnsAfterWind: [{ key: 'gust', header: 'Gust (kt)' }],
  },
  {
    key: 'observedPosition',
    title: 'Hurricane Observed Position',
    windLabel: 'Wind Speed (kt)',
    layerPattern: /observed position/i,
    windField: 'INTENSITY',
    rankField: 'DTG',
    columnsBeforeWind: [{ key: 'date', header: 'Date' }],
    columnsAfterWind: [{ key: 'ss', header: 'SS' }],
  },
];

/** Development types shown in the Forecast Position pie chart (`TCDVLP`). */
export const HURRICANE_DEVELOPMENT_TYPES = [
  'Tropical Storm',
  'Hurricane',
  'Major Hurricane',
  'Subtropical Depression',
] as const;

/**
 * Forecast Position chart: one slice per development type in a fixed order
 * (zero counts stay in the legend); any other `TCDVLP` value is ignored.
 */
export const buildForecastDevelopmentChart = (
  cardFeatures: LayerAnalyticsFeature[]
): LayerAnalyticsChart => {
  const counts = Object.fromEntries(
    HURRICANE_DEVELOPMENT_TYPES.map((type) => [type, 0])
  );
  cardFeatures.forEach(({ attributes }) => {
    const value = toText(attributes.TCDVLP).trim().toLowerCase();
    const type = HURRICANE_DEVELOPMENT_TYPES.find(
      (candidate) => candidate.toLowerCase() === value
    );
    if (type) counts[type] += 1;
  });
  return {
    title: 'Development / type',
    slices: HURRICANE_DEVELOPMENT_TYPES.map((label) => ({
      label,
      count: counts[label],
    })),
  };
};

/** Storm type codes (`STORMTYPE`) of the cone layer, in display order. */
const STORM_TYPE_LABELS: Record<string, string> = {
  TS: 'Tropical Storm',
  HU: 'Hurricane',
  MH: 'Major Hurricane',
  STD: 'Subtropical Depression',
};

const UNKNOWN_STORM_TYPE = 'Unknown';

/** Display label for a `STORMTYPE` code; blank or unrecognised is "Unknown". */
const toStormTypeLabel = (code: unknown): string =>
  STORM_TYPE_LABELS[toText(code).trim().toUpperCase()] ?? UNKNOWN_STORM_TYPE;

const incrementCount = (counts: Record<string, number>, key: string) => {
  counts[key] = (counts[key] ?? 0) + 1;
};

/**
 * Forecast Cone chart: cone features per storm type, only the types that occur,
 * in a fixed order; blank or unrecognised codes are "Unknown".
 */
export const buildConeStormTypeChart = (
  cardFeatures: LayerAnalyticsFeature[]
): LayerAnalyticsChart => {
  const counts: Record<string, number> = {};
  cardFeatures.forEach(({ attributes }) => {
    incrementCount(counts, toStormTypeLabel(attributes.STORMTYPE));
  });
  return {
    title: 'Storm type',
    slices: [...Object.values(STORM_TYPE_LABELS), UNKNOWN_STORM_TYPE]
      .map((label) => ({ label, count: counts[label] ?? 0 }))
      .filter(({ count }) => count > 0),
  };
};

/**
 * Forecast Cone chart: cone features per forecast period (hours), shortest
 * first; features without a period are skipped.
 */
export const buildConeForecastPeriodChart = (
  cardFeatures: LayerAnalyticsFeature[]
): LayerAnalyticsChart => {
  const counts: Record<string, number> = {};
  cardFeatures.forEach(({ attributes }) => {
    const hours = toFiniteNumber(attributes.FCSTPRD);
    if (hours !== null) incrementCount(counts, String(hours));
  });
  return {
    title: 'Forecast period',
    slices: Object.entries(counts)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([hours, count]) => ({ label: `${hours} hr`, count })),
  };
};

/** The donut charts shown under a card's figures, in display order. */
export const buildHurricaneCharts = (
  key: HurricaneCardKey,
  cardFeatures: LayerAnalyticsFeature[]
): LayerAnalyticsChart[] => {
  switch (key) {
    case 'cone':
      return [
        buildConeStormTypeChart(cardFeatures),
        buildConeForecastPeriodChart(cardFeatures),
      ];
    case 'forecastPosition':
      return [buildForecastDevelopmentChart(cardFeatures)];
    case 'observedPosition':
      return [];
  }
};

export const formatHurricaneWind = (knots: number | null): string =>
  knots === null ? '' : String(Math.round(knots));

/** The service reports "no data" as 0 or 9999. */
const toWindKnots = (value: unknown): number | null => {
  const knots = toFiniteNumber(value);
  return knots !== null && knots > 0 && knots < 999 ? knots : null;
};

/** The highest usable value of `field` over the features; null if none. */
const maxOf = (
  cardFeatures: LayerAnalyticsFeature[],
  field: string,
  toValue: (value: unknown) => number | null = toWindKnots
): number | null => {
  const values = cardFeatures
    .map(({ attributes }) => toValue(attributes[field]))
    .filter((value): value is number => value !== null);
  return values.length === 0 ? null : Math.max(...values);
};

const knots = (label: string, value: number | null): LayerAnalyticsFigure => ({
  label,
  value,
  unit: 'kt',
});

/**
 * The labelled figures shown above a card's table. `storms` is the one-per-
 * storm list; `cardFeatures` is every feature of the card's sublayer.
 */
export const buildHurricaneFigures = (
  key: HurricaneCardKey,
  storms: HurricaneStorm[],
  cardFeatures: LayerAnalyticsFeature[]
): LayerAnalyticsFigure[] => {
  switch (key) {
    case 'cone':
      return [
        { label: 'Storms', value: storms.length },
        knots('Max Wind', maxOf(cardFeatures, 'MAX_WIND')),
        knots('Max Gust', maxOf(cardFeatures, 'MAX_GUST')),
        // Saffir-Simpson category: 0 is a real value, so it is not "no data".
        {
          label: 'Max Surge',
          value: maxOf(cardFeatures, 'MAX_SS', toFiniteNumber),
        },
      ];
    case 'forecastPosition':
      return [
        { label: 'Forecast Points', value: cardFeatures.length },
        knots('Max Wind', maxOf(cardFeatures, 'MAXWIND')),
        knots('Max Gust', maxOf(cardFeatures, 'GUST')),
      ];
    case 'observedPosition':
      return [
        { label: 'Observed Points', value: cardFeatures.length },
        // Each storm's latest observation, then the strongest of those.
        knots(
          'Latest Max Wind',
          storms.reduce<number | null>(
            (max, { windKnots }) =>
              windKnots === null ? max : Math.max(max ?? windKnots, windKnots),
            null
          )
        ),
      ];
  }
};

/** An epoch-millisecond timestamp as `YYYY-MM-DD HH:mm UTC`; blank if invalid. */
const formatUtcTimestamp = (value: unknown): string => {
  const millis = toFiniteNumber(value);
  if (millis === null) return '';
  const date = new Date(millis);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};

/** Text for the extra table columns of one card (see `HURRICANE_CARDS`). */
const toHurricaneDetails = (
  key: HurricaneCardKey,
  attributes: Record<string, unknown>
): Record<string, string> => {
  switch (key) {
    case 'cone':
      return {
        stormType: toStormTypeLabel(attributes.STORMTYPE),
        advisory: toText(attributes.ADVISNUM).trim(),
      };
    case 'forecastPosition':
      return {
        // The forecast point's own valid time, already in the storm's time zone.
        date: toText(attributes.FLDATELBL || attributes.DATELBL).trim(),
        gust: formatHurricaneWind(toWindKnots(attributes.GUST)),
      };
    case 'observedPosition':
      return {
        date: formatUtcTimestamp(attributes.DTG),
        // Saffir-Simpson category: 0 is a real value, so it is shown.
        ss: toText(attributes.SS).trim(),
      };
  }
};

const toHurricaneStorm = (
  key: HurricaneCardKey,
  { attributes, longitude, latitude, extent }: LayerAnalyticsFeature,
  windField: string
): HurricaneStorm => ({
  name: toText(attributes.STORMNAME),
  windKnots: toWindKnots(attributes[windField]),
  details: toHurricaneDetails(key, attributes),
  longitude: longitude ?? null,
  latitude: latitude ?? null,
  extent: extent ?? null,
});

/**
 * One feature per storm: the one with the highest `rank`. Forecast layers hold
 * several features per storm (one per forecast period) and the observed layer
 * holds the storm's whole track.
 */
const pickOnePerStorm = (
  features: LayerAnalyticsFeature[],
  rank: (feature: LayerAnalyticsFeature) => number
): LayerAnalyticsFeature[] => {
  const best = new Map<string, LayerAnalyticsFeature>();
  features.forEach((feature) => {
    const { STORMNAME, STORMNUM } = feature.attributes;
    const key = `${toText(STORMNAME).toLowerCase()}|${toText(STORMNUM)}`;
    const current = best.get(key);
    if (!current || rank(feature) > rank(current)) best.set(key, feature);
  });
  return Array.from(best.values());
};

export const isHurricaneFeature = ({ layerTitle }: LayerAnalyticsFeature) =>
  !!layerTitle &&
  HURRICANE_CARDS.some(({ layerPattern }) => layerPattern.test(layerTitle));

type HurricaneCardConfig = (typeof HURRICANE_CARDS)[number];

/** The features of the card's sublayer. */
const pickCardFeatures = (
  { layerPattern }: HurricaneCardConfig,
  features: LayerAnalyticsFeature[]
): LayerAnalyticsFeature[] =>
  features.filter(
    ({ layerTitle }) => !!layerTitle && layerPattern.test(layerTitle)
  );

/** One storm per row, as `HURRICANE_CARDS` describes for the card. */
export const buildHurricaneStorms = (
  { key, windField, rankField }: HurricaneCardConfig,
  cardFeatures: LayerAnalyticsFeature[]
): HurricaneStorm[] => {
  const rank =
    key === 'observedPosition'
      ? ({ attributes }: LayerAnalyticsFeature) =>
          toFiniteNumber(attributes[rankField]) ?? 0
      : ({ attributes }: LayerAnalyticsFeature) =>
          toWindKnots(attributes[rankField]) ?? -1;
  return pickOnePerStorm(cardFeatures, rank).map((feature) =>
    toHurricaneStorm(key, feature, windField)
  );
};

/** The card's table: name, the columns before the wind, wind, then the rest. */
export const buildHurricaneTable = (
  { windLabel, columnsBeforeWind, columnsAfterWind }: HurricaneCardConfig,
  storms: HurricaneStorm[]
): LayerAnalyticsTable => ({
  title: null,
  nameLabel: 'Name',
  columns: [
    ...columnsBeforeWind.map(({ key, header }) => ({ key, label: header })),
    { key: 'wind', label: windLabel },
    ...columnsAfterWind.map(({ key, header }) => ({ key, label: header })),
  ],
  rows: storms.map((storm) => ({
    name: storm.name,
    cells: { ...storm.details, wind: formatHurricaneWind(storm.windKnots) },
    ...toRowLocation(storm),
  })),
});

export const buildHurricaneCard = (
  config: HurricaneCardConfig,
  features: LayerAnalyticsFeature[]
): LayerAnalyticsCard => {
  const cardFeatures = pickCardFeatures(config, features);
  const storms = buildHurricaneStorms(config, cardFeatures);
  return {
    key: config.key,
    title: config.title,
    figures: buildHurricaneFigures(config.key, storms, cardFeatures),
    charts: buildHurricaneCharts(config.key, cardFeatures),
    table: buildHurricaneTable(config, storms),
  };
};

export const buildHurricaneResult = ({
  layerValue,
  label,
  features,
}: {
  layerValue: string;
  label: string;
  features: LayerAnalyticsFeature[];
}): LayerAnalyticsResult => {
  const hurricaneFeatures = features.filter(isHurricaneFeature);
  return {
    layerValue,
    label,
    hazard: 'hurricane',
    featureCount: hurricaneFeatures.length,
    cards:
      hurricaneFeatures.length === 0
        ? []
        : HURRICANE_CARDS.map((config) =>
            buildHurricaneCard(config, hurricaneFeatures)
          ),
  };
};
